import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BankTransferConfig } from '../../../core/models/payment.model';
import { PriceService } from '../../../core/services/price.service';

/**
 * Presentational component that renders the EU vs BR bank-transfer
 * coordinates returned by GET /api/payments/bank-transfer/orders/:orderId/config.
 *
 * Used in two places:
 *  - The payment-processing dialog at checkout (wraps it with copy/close buttons).
 *  - The order-details page reached from the order confirmation email
 *    (read-only display alongside the order summary).
 *
 * Every value has an inline copy icon — useful on both surfaces since
 * the customer is typing these into a banking app.
 */
@Component({
  selector: 'app-bank-transfer-details',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    <div class="info-rows" [class.compact]="compact()">
      @if (isEU()) {
        @if (config().beneficiaryName) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.BENEFICIARY' | translate }}</span>
            <span class="value">{{ config().beneficiaryName }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'beneficiaryName' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('BENEFICIARY', 'beneficiaryName')"
                    (click)="copy('beneficiaryName', config().beneficiaryName)">
              <mat-icon>{{ copiedKey() === 'beneficiaryName' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().iban) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.IBAN' | translate }}</span>
            <span class="value monospace">{{ config().iban }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'iban' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('IBAN', 'iban')"
                    (click)="copy('iban', config().iban)">
              <mat-icon>{{ copiedKey() === 'iban' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().nib) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.NIB' | translate }}</span>
            <span class="value monospace">{{ config().nib }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'nib' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('NIB', 'nib')"
                    (click)="copy('nib', config().nib)">
              <mat-icon>{{ copiedKey() === 'nib' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().accountNumber) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.ACCOUNT' | translate }}</span>
            <span class="value monospace">{{ config().accountNumber }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'accountNumber' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('ACCOUNT', 'accountNumber')"
                    (click)="copy('accountNumber', config().accountNumber)">
              <mat-icon>{{ copiedKey() === 'accountNumber' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().swift) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.SWIFT' | translate }}</span>
            <span class="value monospace">{{ config().swift }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'swift' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('SWIFT', 'swift')"
                    (click)="copy('swift', config().swift)">
              <mat-icon>{{ copiedKey() === 'swift' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
      } @else {
        @if (config().sellerName) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.SELLER_NAME' | translate }}</span>
            <span class="value">{{ config().sellerName }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'sellerName' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('SELLER_NAME', 'sellerName')"
                    (click)="copy('sellerName', config().sellerName)">
              <mat-icon>{{ copiedKey() === 'sellerName' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().sellerCnpj) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.CNPJ' | translate }}</span>
            <span class="value monospace">{{ config().sellerCnpj }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'sellerCnpj' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('CNPJ', 'sellerCnpj')"
                    (click)="copy('sellerCnpj', config().sellerCnpj)">
              <mat-icon>{{ copiedKey() === 'sellerCnpj' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().bank) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.BANK' | translate }}</span>
            <span class="value">{{ config().bank }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'bank' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('BANK', 'bank')"
                    (click)="copy('bank', config().bank)">
              <mat-icon>{{ copiedKey() === 'bank' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().agency) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.AGENCY' | translate }}</span>
            <span class="value monospace">{{ config().agency }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'agency' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('AGENCY', 'agency')"
                    (click)="copy('agency', config().agency)">
              <mat-icon>{{ copiedKey() === 'agency' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
        @if (config().account) {
          <div class="info-row">
            <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.ACCOUNT' | translate }}</span>
            <span class="value monospace">{{ config().account }}</span>
            <button type="button" class="copy-icon"
                    [matTooltip]="(copiedKey() === 'account' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                    [attr.aria-label]="ariaLabelFor('ACCOUNT', 'account')"
                    (click)="copy('account', config().account)">
              <mat-icon>{{ copiedKey() === 'account' ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
        }
      }
      @if (amountNumber() > 0) {
        <div class="info-row">
          <span class="label">{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.AMOUNT' | translate }}</span>
          <span class="value amount">{{ priceService.formatForCurrencyId(amountNumber(), currencyId()) }}</span>
          <button type="button" class="copy-icon"
                  [matTooltip]="(copiedKey() === 'amount' ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY') | translate"
                  [attr.aria-label]="ariaLabelFor('AMOUNT', 'amount')"
                  (click)="copy('amount', amountNumber().toFixed(2))">
            <mat-icon>{{ copiedKey() === 'amount' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    .info-rows {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
    }
    .info-rows.compact {
      background: transparent;
      padding: 0;
      .info-row {
        padding: 4px 0;
        gap: 6px;
        &:not(:last-child) { border-bottom: 1px dashed #e0e0e0; }
        .label { font-size: 11px; }
        .value { font-size: 12px; }
        .monospace { font-size: 11px; letter-spacing: 0; }
      }
      .copy-icon {
        padding: 2px;
        mat-icon {
          font-size: 14px;
          width: 14px;
          height: 14px;
        }
      }
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      gap: 12px;
      &:not(:last-child) { border-bottom: 1px solid #e0e0e0; }
      .label { color: #666; font-size: 14px; flex-shrink: 0; }
      .value { font-weight: 500; font-size: 15px; text-align: right; word-break: break-all; flex: 1; }
      .monospace { font-family: monospace; letter-spacing: 0.5px; }
      .amount { color: #2e7d32; font-size: 16px; font-weight: 700; }
    }
    .copy-icon {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #666;
      flex-shrink: 0;
      &:hover { background: rgba(0, 0, 0, 0.06); color: #2e5339; }
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }
  `]
})
export class BankTransferDetailsComponent {
  private translate = inject(TranslateService);
  protected priceService = inject(PriceService);

  config = input.required<BankTransferConfig>();
  isEU = input.required<boolean>();
  // When true, render in a compressed layout suitable for embedding inside
  // a small box (e.g. the order-details payment-box). Drops the grey bg/
  // padding and shrinks font sizes.
  compact = input<boolean>(false);
  // Optional order total. When supplied, renders as the last row so the
  // customer can copy the exact amount into their banking app — parallel
  // to the Multibanco block's Amount row.
  amount = input<number | string | undefined>(undefined);
  // Optional currencyId of the order. When supplied, the amount is
  // formatted against THAT currency (not the viewer's session currency).
  // Lets staff viewing a BR order from a PT session still see BRL
  // formatting on the bank-transfer Montante row.
  currencyId = input<number | null | undefined>(undefined);

  // Backend may send the amount as a 6-decimal string or as a number.
  // Coerce once.
  amountNumber = computed(() => {
    const raw = this.amount();
    if (raw == null || raw === '') return 0;
    const n = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  });

  // Tracks which field was last copied so a checkmark briefly replaces
  // the copy icon as feedback.
  copiedKey = signal<string | null>(null);

  /**
   * Build an accessible label for a copy icon, e.g. "Copy IBAN".
   * Tooltip alone isn't enough for screen readers — the icon-only button
   * needs an explicit name.
   */
  ariaLabelFor(labelKey: string, fieldKey: string): string {
    const fieldLabel = this.translate.instant(`PAYMENT_PROCESSING.BANK_TRANSFER.${labelKey}`);
    const copyLabel = this.translate.instant(
      this.copiedKey() === fieldKey ? 'PAYMENT_PROCESSING.BANK_TRANSFER.COPIED' : 'COMMON.COPY'
    );
    return `${copyLabel} ${fieldLabel}`;
  }

  async copy(key: string, value: string | undefined): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.copiedKey.set(key);
      setTimeout(() => {
        if (this.copiedKey() === key) {
          this.copiedKey.set(null);
        }
      }, 2000);
    } catch {
      // Clipboard write can fail in non-secure contexts — silent no-op
    }
  }
}
